import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import ComingSoonBanner from '../components/ui/ComingSoonBanner';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  publishDate: string;
  category: string;
  readTime: string;
  image?: string;
  tags: string[];
  isFeatured?: boolean;
}

const BlogPage: React.FC = () => {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const blogPosts: BlogPost[] = [
    {
      id: '1',
      title: t('blog.posts.post1.title', 'Understanding Class Action Lawsuits: A Complete Guide'),
      excerpt: t('blog.posts.post1.excerpt', 'Learn everything you need to know about class action lawsuits, from how they work to when you should consider joining one.'),
      content: t('blog.posts.post1.content', 'Class action lawsuits are a powerful legal tool that allows groups of people with similar claims to band together and pursue justice collectively...'),
      author: t('blog.posts.post1.author', 'Sarah Johnson'),
      publishDate: '2024-01-15',
      category: 'legal-guides',
      readTime: '8 min read',
      tags: ['class actions', 'legal guide', 'lawsuits'],
      isFeatured: true
    },
    {
      id: '2',
      title: t('blog.posts.post2.title', 'The Rise of AI in Legal Services: What You Need to Know'),
      excerpt: t('blog.posts.post2.excerpt', 'Discover how artificial intelligence is transforming the legal industry and making legal services more accessible to everyone.'),
      content: t('blog.posts.post2.content', 'Artificial intelligence is revolutionizing the legal industry, from document automation to predictive analytics...'),
      author: t('blog.posts.post2.author', 'Michael Chen'),
      publishDate: '2024-01-10',
      category: 'technology',
      readTime: '6 min read',
      tags: ['AI', 'legal tech', 'innovation']
    },
    {
      id: '3',
      title: t('blog.posts.post3.title', 'Data Privacy Laws: Your Rights and How to Protect Them'),
      excerpt: t('blog.posts.post3.excerpt', 'An overview of data privacy laws and practical steps you can take to protect your personal information.'),
      content: t('blog.posts.post3.content', 'In today\'s digital age, protecting your personal data has never been more important...'),
      author: t('blog.posts.post3.author', 'Emily Rodriguez'),
      publishDate: '2024-01-08',
      category: 'privacy',
      readTime: '10 min read',
      tags: ['privacy', 'data protection', 'GDPR']
    },
    {
      id: '4',
      title: t('blog.posts.post4.title', 'Employment Law Updates: What Changed in 2024'),
      excerpt: t('blog.posts.post4.excerpt', 'Stay informed about the latest changes in employment law and how they might affect you or your business.'),
      content: t('blog.posts.post4.content', 'The employment law landscape is constantly evolving, with new regulations and court decisions shaping the workplace...'),
      author: t('blog.posts.post4.author', 'David Thompson'),
      publishDate: '2024-01-05',
      category: 'employment',
      readTime: '7 min read',
      tags: ['employment law', 'workplace rights', '2024 updates']
    },
    {
      id: '5',
      title: t('blog.posts.post5.title', 'Small Claims Court: A Step-by-Step Guide'),
      excerpt: t('blog.posts.post5.excerpt', 'Everything you need to know about filing a case in small claims court, from preparation to judgment.'),
      content: t('blog.posts.post5.content', 'Small claims court is designed to provide a simple, inexpensive way to resolve disputes...'),
      author: t('blog.posts.post5.author', 'Lisa Wang'),
      publishDate: '2024-01-03',
      category: 'legal-guides',
      readTime: '12 min read',
      tags: ['small claims', 'court procedures', 'legal guide']
    },
    {
      id: '6',
      title: t('blog.posts.post6.title', 'Consumer Rights: How to Fight Back Against Unfair Practices'),
      excerpt: t('blog.posts.post6.excerpt', 'Learn about your consumer rights and how to take action when businesses engage in unfair or deceptive practices.'),
      content: t('blog.posts.post6.content', 'As a consumer, you have rights that protect you from unfair business practices...'),
      author: t('blog.posts.post6.author', 'Robert Kim'),
      publishDate: '2024-01-01',
      category: 'consumer-rights',
      readTime: '9 min read',
      tags: ['consumer rights', 'unfair practices', 'legal action']
    }
  ];

  const categories = [
    { id: 'all', name: t('blog.categories.all', 'All Posts') },
    { id: 'legal-guides', name: t('blog.categories.legalGuides', 'Legal Guides') },
    { id: 'technology', name: t('blog.categories.technology', 'Technology') },
    { id: 'privacy', name: t('blog.categories.privacy', 'Privacy & Data') },
    { id: 'employment', name: t('blog.categories.employment', 'Employment Law') },
    { id: 'consumer-rights', name: t('blog.categories.consumerRights', 'Consumer Rights') }
  ];

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    const matchesSearch = !searchTerm || 
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesCategory && matchesSearch;
  });

  const featuredPosts = blogPosts.filter(post => post.isFeatured);
  const regularPosts = filteredPosts.filter(post => !post.isFeatured);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t('blog.title', 'Legal Blog & Insights')}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('blog.subtitle', 'Stay informed with the latest legal news, guides, and insights from our team of legal experts.')}
          </p>
        </div>

        {/* Coming Soon Banner */}
        <ComingSoonBanner />

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('blog.search', 'Search')}
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('blog.searchPlaceholder', 'Search articles...')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>

            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('blog.category', 'Category')}
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
              >
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Results Count */}
            <div className="flex items-end">
              <p className="text-sm text-gray-600">
                {t('blog.results', '{{count}} articles found', { count: filteredPosts.length })}
              </p>
            </div>
          </div>
        </div>

        {/* Featured Posts */}
        {!searchTerm && selectedCategory === 'all' && featuredPosts.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {t('blog.featured', 'Featured Articles')}
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {featuredPosts.map((post) => (
                <div key={post.id} className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-200">
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <span className="bg-cyan-100 text-cyan-800 text-xs font-medium px-2 py-1 rounded-full">
                        {t('blog.featured', 'Featured')}
                      </span>
                      <span className="ml-2 text-sm text-gray-500">{post.readTime}</span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-gray-300 rounded-full mr-3"></div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{post.author}</p>
                          <p className="text-sm text-gray-500">{formatDate(post.publishDate)}</p>
                        </div>
                      </div>
                      <Link
                        to={`/blog/${post.id}`}
                        className="text-cyan-600 hover:text-cyan-700 text-sm font-medium"
                      >
                        {t('blog.readMore', 'Read more')} →
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Regular Posts */}
        {filteredPosts.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">📝</div>
            <h3 className="text-xl font-medium text-gray-900 mb-2">
              {t('blog.noResults', 'No articles found')}
            </h3>
            <p className="text-gray-600">
              {t('blog.noResultsDescription', 'Try adjusting your search criteria or category filter.')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {regularPosts.map((post) => (
              <div key={post.id} className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-200">
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <span className="bg-gray-100 text-gray-800 text-xs font-medium px-2 py-1 rounded-full">
                      {categories.find(cat => cat.id === post.category)?.name}
                    </span>
                    <span className="ml-2 text-sm text-gray-500">{post.readTime}</span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 mb-4 text-sm">
                    {post.excerpt}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {post.tags.slice(0, 3).map((tag, index) => (
                      <span key={index} className="bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-6 h-6 bg-gray-300 rounded-full mr-2"></div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{post.author}</p>
                        <p className="text-xs text-gray-500">{formatDate(post.publishDate)}</p>
                      </div>
                    </div>
                    <Link
                      to={`/blog/${post.id}`}
                      className="text-cyan-600 hover:text-cyan-700 text-sm font-medium"
                    >
                      {t('blog.readMore', 'Read more')} →
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Newsletter Signup */}
        <div className="mt-16 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-lg p-8 text-center text-white">
          <h2 className="text-2xl font-bold mb-4">
            {t('blog.newsletter.title', 'Stay Updated')}
          </h2>
          <p className="text-lg mb-6 opacity-90">
            {t('blog.newsletter.description', 'Subscribe to our newsletter for the latest legal insights and updates.')}
          </p>
          <div className="max-w-md mx-auto">
            <div className="flex">
              <input
                type="email"
                placeholder={t('blog.newsletter.placeholder', 'Enter your email')}
                className="flex-1 px-4 py-3 rounded-l-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-white"
              />
              <button className="bg-white text-cyan-600 px-6 py-3 rounded-r-md font-medium hover:bg-gray-100 transition-colors duration-200">
                {t('blog.newsletter.subscribe', 'Subscribe')}
              </button>
            </div>
          </div>
        </div>

        {/* Popular Topics */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">
            {t('blog.popularTopics', 'Popular Topics')}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {['Class Actions', 'Data Privacy', 'Employment Law', 'Consumer Rights'].map((topic) => (
              <Link
                key={topic}
                to={`/blog?category=${topic.toLowerCase().replace(' ', '-')}`}
                className="bg-white rounded-lg shadow-sm p-6 text-center hover:shadow-md transition-shadow duration-200"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {topic}
                </h3>
                <p className="text-sm text-gray-600">
                  {t('blog.topicDescription', 'Latest articles and insights')}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPage;